import { Routes } from '@angular/router';
import { lazy } from './core/i18n/lazy-page';
import { lazyGuard } from './core/guards/lazy-guard';

// Os guards são carregados sob demanda: importados aqui de forma estática, eles arrastavam
// o AuthService e o `aws-amplify` (37 KB transferidos) para o bundle inicial de toda rota,
// inclusive das públicas e da /oferta, que não têm guard. Ver core/guards/lazy-guard.ts.
const authGuard = lazyGuard(() => import('./core/guards/auth.guard').then((m) => m.authGuard));
const guestGuard = lazyGuard(() => import('./core/guards/guest.guard').then((m) => m.guestGuard));
const cartaoGuard = lazyGuard(() =>
  import('./core/guards/cartao.guard').then((m) => m.cartaoGuard),
);
const inativoGuard = lazyGuard(() =>
  import('./core/guards/inativo.guard').then((m) => m.inativoGuard),
);

// O 1º argumento de lazy() é o bundle de tradução da rota: baixa em paralelo com o chunk
// e é gerado por scripts/split-i18n.mjs a partir dos namespaces que a página usa.
export const routes: Routes = [
  {
    path: '',
    canActivate: [guestGuard],
    loadComponent: lazy('home', () => import('./pages/home/home').then((m) => m.HomeComponent)),
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: lazy('login', () => import('./pages/login/login').then((m) => m.LoginComponent)),
  },
  {
    path: 'cadastro',
    canActivate: [guestGuard],
    loadComponent: lazy('cadastro', () =>
      import('./pages/cadastro/cadastro').then((m) => m.CadastroComponent),
    ),
  },
  {
    path: 'confirmar-usuario',
    canActivate: [guestGuard],
    loadComponent: lazy('confirmar-usuario', () =>
      import('./pages/confirmar-usuario/confirmar-usuario').then(
        (m) => m.ConfirmarUsuarioComponent,
      ),
    ),
  },
  {
    path: 'esqueci-senha',
    canActivate: [guestGuard],
    loadComponent: lazy('esqueci-senha', () =>
      import('./pages/esqueci-senha/esqueci-senha').then((m) => m.EsqueciSenhaComponent),
    ),
  },
  {
    path: 'redefinir-senha',
    canActivate: [guestGuard],
    loadComponent: lazy('redefinir-senha', () =>
      import('./pages/redefinir-senha/redefinir-senha').then((m) => m.RedefinirSenhaComponent),
    ),
  },
  {
    path: 'termos-de-uso',
    loadComponent: lazy('termos-de-uso', () =>
      import('./pages/termos-de-uso/termos-de-uso').then((m) => m.TermosDeUsoComponent),
    ),
  },
  {
    path: 'privacidade',
    loadComponent: lazy('privacidade', () =>
      import('./pages/privacidade/privacidade').then((m) => m.PrivacidadeComponent),
    ),
  },
  {
    path: 'contato',
    loadComponent: lazy('contato', () =>
      import('./pages/contato/contato').then((m) => m.ContatoComponent),
    ),
  },
  {
    path: 'faq',
    loadComponent: lazy('faq', () => import('./pages/faq/faq').then((m) => m.FaqComponent)),
  },
  // ───────── Landing de campanha paga (noindex, fora do nav/footer) ─────────
  // Sem guard de propósito: o link vem de anúncio e precisa abrir também para quem já está logado.
  {
    path: 'oferta',
    loadComponent: lazy('oferta', () =>
      import('./pages/campanhas/oferta/oferta').then((m) => m.OfertaComponent),
    ),
  },
  // ───────── Páginas públicas de recurso (SEO/GEO) ─────────
  {
    path: 'fotos-de-produto-com-ia',
    loadComponent: lazy('recursos-fotos', () =>
      import('./pages/recursos/fotos-de-produto-com-ia/fotos-de-produto-com-ia').then(
        (m) => m.FotosDeProdutoComIaComponent,
      ),
    ),
  },
  {
    path: 'catalogo-virtual',
    loadComponent: lazy('recursos-catalogo', () =>
      import('./pages/recursos/catalogo-virtual/catalogo-virtual').then(
        (m) => m.CatalogoVirtualComponent,
      ),
    ),
  },
  {
    path: 'integracao-com-marketplaces',
    loadComponent: lazy('recursos-marketplaces', () =>
      import('./pages/recursos/integracao-com-marketplaces/integracao-com-marketplaces').then(
        (m) => m.IntegracaoComMarketplacesComponent,
      ),
    ),
  },
  {
    path: 'galeria-de-imagens',
    loadComponent: lazy('recursos-galeria', () =>
      import('./pages/recursos/galeria-de-imagens/galeria-de-imagens').then(
        (m) => m.GaleriaDeImagensComponent,
      ),
    ),
  },
  {
    path: 'caixa-de-entrada-unificada',
    loadComponent: lazy('recursos-caixa', () =>
      import('./pages/recursos/caixa-de-entrada-unificada/caixa-de-entrada-unificada').then(
        (m) => m.CaixaDeEntradaUnificadaComponent,
      ),
    ),
  },
  {
    path: 'copywriter-para-redes-sociais',
    loadComponent: lazy('recursos-copywriter', () =>
      import('./pages/recursos/copywriter-para-redes-sociais/copywriter-para-redes-sociais').then(
        (m) => m.CopywriterParaRedesSociaisComponent,
      ),
    ),
  },
  {
    path: 'configuracao-inicial',
    canActivate: [authGuard],
    loadComponent: lazy('configuracao-inicial', () =>
      import('./pages/configuracao-inicial/configuracao-inicial').then(
        (m) => m.ConfiguracaoInicialComponent,
      ),
    ),
  },
  {
    path: 'completar-cadastro',
    canActivate: [authGuard],
    loadComponent: lazy('completar-cadastro', () =>
      import('./pages/completar-cadastro/completar-cadastro').then(
        (m) => m.CompletarCadastroComponent,
      ),
    ),
  },
  {
    path: 'checkout-planos',
    canActivate: [authGuard],
    loadComponent: lazy('checkout-planos', () =>
      import('./pages/checkout-planos/checkout-planos').then((m) => m.CheckoutPlanosComponent),
    ),
  },
  {
    path: 'checkout-tokens',
    canActivate: [authGuard],
    loadComponent: lazy('checkout-tokens', () =>
      import('./pages/checkout-tokens/checkout-tokens').then((m) => m.CheckoutTokensComponent),
    ),
  },
  {
    path: 'aguardando-ativacao',
    canActivate: [authGuard, inativoGuard],
    loadComponent: lazy('aguardando-ativacao', () =>
      import('./pages/aguardando-ativacao/aguardando-ativacao').then(
        (m) => m.AguardandoAtivacaoComponent,
      ),
    ),
  },
  {
    path: '',
    canActivate: [authGuard, cartaoGuard],
    loadComponent: lazy('layout', () =>
      import('./shared/layout/layout').then((m) => m.LayoutComponent),
    ),
    children: [
      {
        path: 'inicio',
        loadComponent: lazy('inicio', () =>
          import('./pages/inicio/inicio').then((m) => m.InicioComponent),
        ),
      },
      {
        path: 'studio-ia',
        loadComponent: lazy('studio-ia', () =>
          import('./pages/studio-ia/studio-ia').then((m) => m.StudioIaComponent),
        ),
      },
      {
        path: 'cenarios-personalizados',
        loadComponent: lazy('cenarios-personalizados', () =>
          import('./pages/cenarios-personalizados/cenarios-personalizados').then(
            (m) => m.CenariosPersonalizadosComponent,
          ),
        ),
      },
      {
        path: 'copywriter',
        loadComponent: lazy('copywriter', () =>
          import('./pages/copywriter/copywriter').then((m) => m.CopywriterComponent),
        ),
      },
      {
        path: 'catalogo',
        loadComponent: lazy('catalogo', () =>
          import('./pages/catalogo/catalogo').then((m) => m.CatalogoComponent),
        ),
      },
      {
        path: 'galeria',
        loadComponent: lazy('galeria', () =>
          import('./pages/galeria/galeria').then((m) => m.GaleriaComponent),
        ),
      },
      {
        path: 'redes-sociais',
        loadComponent: lazy('redes-sociais', () =>
          import('./pages/redes-sociais/redes-sociais').then((m) => m.RedesSociaisComponent),
        ),
      },
      {
        path: 'redes-sociais/cronograma',
        loadComponent: lazy('cronograma', () =>
          import('./pages/redes-sociais/cronograma/cronograma').then((m) => m.CronogramaComponent),
        ),
      },
      {
        path: 'redes-sociais/contas',
        loadComponent: lazy('contas-vinculadas', () =>
          import('./pages/redes-sociais/contas-vinculadas/contas-vinculadas').then(
            (m) => m.ContasVinculadasComponent,
          ),
        ),
      },
      {
        path: 'redes-sociais/postagem/nova',
        loadComponent: lazy('nova-postagem', () =>
          import('./pages/redes-sociais/nova-postagem/nova-postagem').then(
            (m) => m.NovaPostagemComponent,
          ),
        ),
      },
      {
        path: 'redes-sociais/metricas',
        loadComponent: lazy('metricas', () =>
          import('./pages/redes-sociais/metricas/metricas').then((m) => m.MetricasComponent),
        ),
      },
      {
        path: 'redes-sociais/plano-semana',
        loadComponent: () =>
          import('./pages/redes-sociais/plano-semana/plano-semana').then(
            (m) => m.PlanoSemanaComponent,
          ),
      },
      {
        path: 'redes-sociais/inbox',
        loadComponent: () =>
          import('./pages/redes-sociais/inbox/inbox').then((m) => m.InboxComponent),
      },
      {
        path: 'redes-sociais/comentarios',
        loadComponent: () =>
          import('./pages/redes-sociais/comentarios/comentarios').then(
            (m) => m.ComentariosComponent,
          ),
      },
      {
        path: 'redes-sociais/mencoes',
        loadComponent: () =>
          import('./pages/redes-sociais/mencoes/mencoes').then((m) => m.MencoesComponent),
      },
      {
        path: 'redes-sociais/audiencia',
        loadComponent: () =>
          import('./pages/redes-sociais/audiencia/audiencia').then((m) => m.AudienciaComponent),
      },
      {
        path: 'redes-sociais/automacoes',
        loadComponent: lazy('automacoes', () =>
          import('./pages/redes-sociais/automacoes/automacoes').then((m) => m.AutomacoesComponent),
        ),
      },
      {
        path: 'mercado-livre',
        loadComponent: lazy('mercado-livre', () =>
          import('./pages/mercado-livre/mercado-livre').then((m) => m.MercadoLivreComponent),
        ),
      },
      {
        path: 'mercado-livre/itens/novo',
        loadComponent: lazy('mercado-livre-item', () =>
          import('./pages/mercado-livre/item-form/item-form').then((m) => m.ItemFormComponent),
        ),
      },
      {
        path: 'mercado-livre/itens/:itemId/editar',
        loadComponent: lazy('mercado-livre-item', () =>
          import('./pages/mercado-livre/item-form/item-form').then((m) => m.ItemFormComponent),
        ),
      },
      {
        path: 'extrato',
        loadComponent: lazy('extrato', () =>
          import('./pages/extrato/extrato').then((m) => m.ExtratoComponent),
        ),
      },
      {
        path: 'minhas-solicitacoes',
        loadComponent: lazy('minhas-solicitacoes', () =>
          import('./pages/minhas-solicitacoes/minhas-solicitacoes').then(
            (m) => m.MinhasSolicitacoesComponent,
          ),
        ),
      },
      {
        path: 'configuracoes',
        loadComponent: lazy('configuracoes', () =>
          import('./pages/configuracoes/configuracoes').then((m) => m.ConfiguracoesComponent),
        ),
      },
      {
        path: '',
        redirectTo: 'inicio',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
